/**
 * tools-depletion-events.ts — CAR-42 wave-2
 *
 * Registers the `subscriber_depletion_events` MCP tool.
 *
 * Reads bundle-depleted events from RATE_LIMIT_KV, which the API worker
 * populates on `last_slice` webhook receipt (key: bundle-depleted:<subscriberId>,
 * TTL 7 days). Returns the most recent depletion event for a subscriber.
 *
 * Also supports listing recent depletions across the fleet by scanning a prefix
 * (requires KV list — returns up to 20 most recent entries).
 *
 * TENANCY
 * -------
 * RATE_LIMIT_KV is one namespace shared by every customer, and the key is the
 * bare OCS subscriber id. Neither the single lookup nor the "fleet-wide" list
 * has any tenant in its key, so for a managed org — where one master token
 * covers every sub-account — both paths returned other customers' subscriber
 * ids, ICCIDs, MSISDNs and plan ids. Ownership is therefore proved per event
 * from the ICCID the webhook stored, using the same preflight the OCS tools use.
 */

import { z } from "zod";
import type { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import type { ToolContext } from "./tools.js";
import { assertManagedIccidAccess, managedOwnAccountId } from "./ocs-scoping.js";

interface BundleDepletedEvent {
  subscriberId: string;
  iccid: string | null;
  msisdn: string | null;
  planId: number;
  depletedAt: string;
}

type ToolResult = { content: Array<{ type: "text"; text: string }>; isError?: boolean };

function ok(payload: unknown): ToolResult {
  return { content: [{ type: "text", text: JSON.stringify(payload, null, 2) }] };
}

function err(message: string): ToolResult {
  return { isError: true, content: [{ type: "text", text: message }] };
}

/**
 * The token to prove ICCID ownership with, or null when no proof is needed.
 *
 * Null for a BYO org: its own token is already the boundary, so a check here
 * would cost an OCS round trip and buy nothing. Resolved once per tool call so
 * the fleet-wide list does not decrypt the token twenty times.
 */
async function ownershipToken(ctx: ToolContext): Promise<string | null> {
  if (managedOwnAccountId(ctx.managedAuth) === undefined) return null;
  return ctx.getUserToken(ctx.props.sub);
}

/**
 * Whether the caller may see an event, judged by the ICCID the webhook stored.
 *
 * The ICCID is the only tenant-bearing field on the record, so an event saved
 * without one cannot be attributed and is withheld rather than guessed.
 */
async function ownsEvent(
  ctx: ToolContext,
  token: string | null,
  iccid: string | null,
): Promise<boolean> {
  if (token === null) return true;
  if (!iccid) return false;
  const access = await assertManagedIccidAccess(
    ctx.env.CARRIER_OCS_BASE_URL,
    { token, ...ctx.managedAuth },
    iccid,
  );
  return access.ok;
}

export function registerDepletionEventsTool(server: McpServer, ctx: ToolContext): void {
  server.registerTool(
    "subscriber_depletion_events",
    {
      title: "Subscriber Bundle Depletion Events",
      description:
        "Look up bundle depletion events for a subscriber (or list recent fleet-wide depletions). " +
        "Returns the most recent bundle.depleted event stored when the prepaid usage webhook " +
        "received a last_slice notification. Events are retained for 7 days. " +
        "Use subscriberId to check a specific subscriber, or omit to list the 20 most recent " +
        "fleet-wide depletions. Requires 'read' scope.",
      inputSchema: {
        subscriberId: z
          .string()
          .optional()
          .describe(
            "OCS subscriber ID to look up. Omit to list the 20 most recent fleet-wide depletions.",
          ),
        since: z
          .string()
          .optional()
          .describe(
            "ISO-8601 timestamp — only return events at or after this time (for single-subscriber lookup).",
          ),
      },
      annotations: { readOnlyHint: true },
    },
    async (args): Promise<ToolResult> => {
      if (!ctx.props.scope.includes("read")) {
        return err(
          `Scope denied: subscriber_depletion_events requires 'read' scope. Your token has: [${ctx.props.scope.join(", ")}].`,
        );
      }

      const kv = ctx.env.RATE_LIMIT_KV;
      if (!kv) {
        // The old text was "Add the binding to wrangler.jsonc and redeploy",
        // which is an instruction for whoever operates the Worker. A person
        // running the published CLI has no wrangler.jsonc and nothing to
        // redeploy, so the message described a fix they could not perform and
        // left them believing the tool was broken.
        return err(
          "subscriber_depletion_events cannot answer here. Depletion events are written " +
            "by the Carrier webhook receiver as they arrive from the network and kept for " +
            "7 days in hosted storage; a local stdio server receives no webhooks and has " +
            "nowhere to keep them, so there is no history on this machine to read. " +
            "There is nothing to set in your environment. Use the hosted MCP at " +
            "https://mcp.carrier.llc/mcp for this tool. To see a subscriber's current " +
            "bundle state instead of its depletion history, `get_subscriber` and " +
            "`list_subscriber_packages` query OCS directly and work here.",
        );
      }

      const { subscriberId, since } = args as { subscriberId?: string; since?: string };

      let scopeToken: string | null;
      try {
        scopeToken = await ownershipToken(ctx);
      } catch (e) {
        return err(
          `Could not resolve the OCS credential needed to scope this read: ${
            e instanceof Error ? e.message : String(e)
          }`,
        );
      }

      // --- Single subscriber lookup ---
      if (subscriberId) {
        const raw = await kv.get(`bundle-depleted:${subscriberId}`, "text");
        if (!raw) {
          return ok({
            subscriber_id: subscriberId,
            depleted: false,
            event: null,
            note: "No bundle depletion event found in the last 7 days.",
          });
        }

        let event: BundleDepletedEvent;
        try {
          event = JSON.parse(raw) as BundleDepletedEvent;
        } catch {
          // malformed KV entry — treat as absent
          return ok({
            subscriber_id: subscriberId,
            depleted: false,
            event: null,
            note: "Stored depletion entry was invalid JSON and was ignored.",
          });
        }

        // Same shape as a genuine miss: a managed caller must not be able to
        // tell "not yours" from "no such event" and enumerate the fleet.
        if (!(await ownsEvent(ctx, scopeToken, event.iccid))) {
          return ok({
            subscriber_id: subscriberId,
            depleted: false,
            event: null,
            note: "No bundle depletion event found in the last 7 days.",
          });
        }

        if (since && event.depletedAt < since) {
          return ok({
            subscriber_id: subscriberId,
            depleted: false,
            event: null,
            note: `No bundle depletion after ${since}.`,
          });
        }

        return ok({
          subscriber_id: subscriberId,
          depleted: true,
          event: {
            subscriber_id: event.subscriberId,
            iccid: event.iccid,
            msisdn: event.msisdn,
            plan_id: event.planId,
            depleted_at: event.depletedAt,
          },
        });
      }

      // --- Fleet-wide list (last 20 depletions) ---
      const listResult = await kv.list({ prefix: "bundle-depleted:", limit: 20 });

      if (listResult.keys.length === 0) {
        return ok({
          depletions: [],
          total: 0,
          note: "No bundle depletion events in the last 7 days.",
        });
      }

      const events: Array<{
        subscriber_id: string;
        iccid: string | null;
        msisdn: string | null;
        plan_id: number;
        depleted_at: string;
      }> = [];

      await Promise.all(
        listResult.keys.map(async (k: { name: string }) => {
          const raw = await kv.get(k.name, "text");
          if (!raw) return;
          try {
            const ev = JSON.parse(raw) as BundleDepletedEvent;
            if (since && ev.depletedAt < since) return;
            // "Fleet-wide" means the caller's fleet. The KV prefix spans every
            // customer, so each entry is attributed before it is returned.
            if (!(await ownsEvent(ctx, scopeToken, ev.iccid))) return;
            events.push({
              subscriber_id: ev.subscriberId,
              iccid: ev.iccid,
              msisdn: ev.msisdn,
              plan_id: ev.planId,
              depleted_at: ev.depletedAt,
            });
          } catch {
            // malformed KV entry — skip
          }
        }),
      );

      events.sort((a, b) => b.depleted_at.localeCompare(a.depleted_at));

      return ok({
        depletions: events,
        total: events.length,
        list_truncated: !listResult.list_complete,
      });
    },
  );
}
